import * as v from 'valibot';
import { marketplaceFieldSCSchema } from './components/MarketplaceField/schema';
import { COLORS_MAP } from './constants';
import type { StructuredContent, StructuredContentDocument } from './hydration/types';
import type { ColorToken } from './types';

export const isStructuredContent = (section: unknown): section is StructuredContentDocument => {
  return (
    section !== null &&
    typeof section !== 'undefined' &&
    typeof section === 'object' &&
    'type' in section &&
    'version' in section &&
    'props' in section
  );
};

export const getAllChildrenStrings = (
  sc: StructuredContentDocument<Record<string, unknown>>,
  prevValue = '',
): string => {
  if (Array.isArray(sc.props.children)) {
    for (const child of sc.props.children) {
      if (typeof child === 'string') {
        prevValue = prevValue.concat(child);
      }
      if (isStructuredContent(child)) {
        const isMarketplaceFieldSC = v.safeParse(marketplaceFieldSCSchema, child.props);
        if (isMarketplaceFieldSC.success) {
          const { marketplaceField } = isMarketplaceFieldSC.output;
          prevValue = prevValue.concat(marketplaceField.value);
        } else {
          prevValue = getAllChildrenStrings(child, prevValue);
        }
      }
    }
  }
  if (isStructuredContent(sc?.props?.children)) {
    prevValue = getAllChildrenStrings(sc?.props?.children, prevValue);
  }
  return prevValue;
};

export const getAllChildrenTypes = (
  sc: StructuredContentDocument<Record<string, unknown>>,
  prevValue: Set<string> = new Set(),
): Set<string> => {
  if (Array.isArray(sc.props.children)) {
    for (const child of sc.props.children) {
      if (isStructuredContent(child)) {
        prevValue.add(child.type);
        prevValue = getAllChildrenTypes(child, prevValue);
      }
    }
  }
  if (isStructuredContent(sc?.props?.children)) {
    prevValue.add(sc.props.children.type);
    prevValue = getAllChildrenTypes(sc.props.children, prevValue);
  }
  return prevValue;
};

export const markNestedTypes = (doc: StructuredContent): StructuredContent => {
  if (!doc || typeof doc === 'string') return doc;
  if (doc.type !== 'Type' && !doc.props.children) return doc;
  return {
    ...doc,
    props: {
      ...doc.props,
      ...(doc.type === 'Type' ? { isNested: true } : {}),
      ...(doc.props.children !== undefined
        ? {
            children: Array.isArray(doc.props.children)
              ? doc.props.children.map(d => markNestedTypes(d))
              : markNestedTypes(doc.props.children),
          }
        : {}),
    },
  };
};

export const getMappedColor = (colorToken: string | null | undefined, defaultColor: string) => {
  if (!colorToken) {
    return defaultColor;
  }
  if (colorToken in COLORS_MAP) {
    return COLORS_MAP[colorToken as ColorToken] || defaultColor;
  }
  return defaultColor;
};
